import React, { ChangeEvent, FormEvent, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, IconButton, styled, useTheme } from '@mui/material';
import { Mic, Send, KeyboardArrowUp } from '@mui/icons-material';
import ChatBubble from './bubble/ChatBubble';
import { useTranslation } from 'react-i18next';
import { FeedbackInterface, ParagraphInterface, MultipleChoiceData } from 'entities/interfaces';
import { SubmitButton } from '../shared/SubmitButton';
import MobileChatLoader from '../mobile/skills/MobileChatLoader';
import MultipleChoice from './MultipleChoice';
import { parseHTMLToJSON } from 'utils/urlDecoder';
import DragAndDrop from './DragAndDropMobile';
import SortTheParagraph from './SortTheParagraphMobile';

const UltraModernWrapper = styled(Box)({
    width: '100%',
    maxWidth: '100vw',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8f9fa',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',

    '@media (max-width: 375px) and (max-height: 667px)': {
        height: '100vh',
    },

    '@media (max-width: 360px) and (max-height: 780px)': {
        height: '100vh',
    },

    '@media (max-width: 320px)': {
        height: '100vh',
    }
});

const ModernContentBox = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    backgroundColor: 'transparent',
    position: 'relative',
    overflow: 'hidden',
    minHeight: 0,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
});

const PremiumChatBox = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flexGrow: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '24px 16px',
    backgroundColor: 'transparent',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',

    '-webkit-overflow-scrolling': 'touch',
    scrollBehavior: 'smooth',

    '&::-webkit-scrollbar': {
        display: 'none'
    },
    scrollbarWidth: 'none',

    '& > *': {
        maxWidth: '100%',
        boxSizing: 'border-box',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
    },

    '@media (max-width: 360px)': {
        padding: '16px 12px',
        gap: '6px',
    },

    '@media (max-width: 320px)': {
        padding: '12px 8px',
        gap: '4px',
    },

    '@media (max-height: 600px)': {
        padding: '16px 12px',
        gap: '6px',
    },

    '@media (max-width: 812px) and (max-height: 375px) and (orientation: landscape)': {
        padding: '8px 12px',
        gap: '4px',
    },

    '&::after': {
        content: '""',
        height: '12px',
        flexShrink: 0,

        '@media (max-width: 360px)': {
            height: '8px',
        },

        '@media (max-width: 320px)': {
            height: '6px',
        }
    }
});
const PremiumUserBox = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '0',
    marginTop: '0',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
});

const PremiumUserContent = styled(Box)({
    backgroundColor: '#A1B7A5',
    color: '#ffffff',
    borderRadius: '20px 20px 4px 20px',
    padding: '12px 16px',
    maxWidth: '80%',
    minWidth: '44px',
    fontSize: '16px',
    lineHeight: '1.4',
    fontWeight: '400',
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    hyphens: 'auto',
    boxShadow: '0 2px 12px rgba(161, 183, 165, 0.25)',
    boxSizing: 'border-box',

    '@media (max-width: 360px)': {
        fontSize: '15px',
        padding: '10px 14px',
        maxWidth: '85%',
        borderRadius: '18px 18px 4px 18px',
        lineHeight: '1.3',
    },
    '@media (max-width: 320px)': {
        fontSize: '14px',
        padding: '8px 12px',
        maxWidth: '88%',
        borderRadius: '16px 16px 3px 16px',
        lineHeight: '1.25',
        minWidth: '40px',
    }
});

const UltraInputContainer = styled(Box)({
    backgroundColor: '#ffffff',
    borderTop: '1px solid #f0f0f0',
    padding: '16px',
    paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
    zIndex: 1000,
    flexShrink: 0,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',

    '@media (max-width: 360px)': {
        padding: '12px',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
    },

    '@media (max-width: 320px)': {
        padding: '10px 8px',
        paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
    },

    '@media (orientation: landscape) and (max-height: 500px)': {
        padding: '8px 12px',
        paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
    }
});

const MinimalDisclaimer = styled(Typography)({
    fontSize: '11px',
    color: '#8e8e93',
    textAlign: 'center',
    paddingBottom: '10px',
    lineHeight: '1.3',
    fontWeight: '400',
    opacity: 0.8,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',

    '@media (max-width: 360px)': {
        fontSize: '10px',
        paddingBottom: '8px',
        lineHeight: '1.2',
    },

    '@media (max-width: 320px)': {
        fontSize: '9px',
        paddingBottom: '6px',
        lineHeight: '1.1',
    },
    '@media (orientation: landscape) and (max-height: 450px)': {
        display: 'none',
    }
});
const PremiumInputWrapper = styled(Box)<{ focused: boolean; enabled: boolean }>(({ focused, enabled }) => ({
    display: 'flex',
    alignItems: 'flex-end',
    gap: '12px',
    backgroundColor: focused ? '#ffffff' : '#f2f2f7',
    borderRadius: '20px',
    padding: '8px 16px',
    minHeight: '44px',
    border: `1.5px solid ${focused ? '#A1B7A5' : 'transparent'}`,
    boxShadow: focused
        ? '0 0 0 4px rgba(161, 183, 165, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1)'
        : '0 1px 3px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',

    ...(enabled && {
        '&:hover': {
            backgroundColor: '#ffffff',
            borderColor: '#A1B7A5',
            boxShadow: '0 0 0 2px rgba(161, 183, 165, 0.05), 0 2px 8px rgba(0, 0, 0, 0.1)',
        }
    }),

    ...(!enabled && {
        backgroundColor: '#f5f5f5',
        borderColor: 'transparent',
        opacity: 0.7,
        cursor: 'not-allowed',
    }),

    '@media (max-width: 360px)': {
        borderRadius: '18px',
        padding: '6px 14px',
        minHeight: '40px',
        gap: '10px',
    },

    '@media (max-width: 320px)': {
        borderRadius: '16px',
        padding: '5px 12px',
        minHeight: '36px',
        gap: '8px',
    },

    '@media (orientation: landscape) and (max-height: 500px)': {
        minHeight: '36px',
        padding: '6px 14px',
    }
}));

const PremiumTextField = styled(TextField)<{ enabled: boolean }>(({ enabled }) => ({
    flex: 1,
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',

    '& .MuiInputBase-root': {
        backgroundColor: 'transparent',
        border: 'none',
        color: enabled ? '#1d1d1f' : '#8e8e93',
        fontSize: '16px',
        lineHeight: '20px',
        fontWeight: '400',
        width: '100%',
        minWidth: 0,

        '&::before, &::after, & fieldset': {
            display: 'none !important',
        },

        '& .MuiInputBase-input': {
            padding: '6px 0',
            color: enabled ? '#1d1d1f' : '#8e8e93',
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',

            '&::placeholder': {
                color: '#a1a1a6',
                opacity: 1,
                fontWeight: '400',
            },

            '&:disabled': {
                color: '#8e8e93',
                '-webkit-text-fill-color': '#8e8e93',
            }
        }
    },

    '& .MuiInput-underline': {
        '&::before, &::after': {
            display: 'none !important',
        }
    },

    '@media (max-width: 390px)': {
        '& .MuiInputBase-root': {
            fontSize: '16px',
        }
    },
    '@media (max-width: 320px)': {
        '& .MuiInputBase-root': {
            fontSize: '15px',
            lineHeight: '18px',

            '& .MuiInputBase-input': {
                padding: '4px 0',
            }
        }
    }
}));
const PremiumActionButton = styled(IconButton)<{ variant?: 'mic' | 'send'; enabled?: boolean }>(({ variant, enabled = true }) => ({
    width: '36px',
    height: '36px',
    padding: '0',
    borderRadius: '50%',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

    ...(variant === 'send' && {
        backgroundColor: '#A1B7A5',
        color: '#ffffff',
        boxShadow: '0 2px 8px rgba(161, 183, 165, 0.3)',

        '&:hover': {
            backgroundColor: '#8FA599',
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(161, 183, 165, 0.4)',
        },

        '&:active': {
            transform: 'scale(0.95)',
        },

        '&:disabled': {
            backgroundColor: '#d1d1d6',
            color: '#8e8e93',
            transform: 'none',
            boxShadow: 'none',
        }
    }),

    ...(variant === 'mic' && {
        backgroundColor: 'transparent',
        color: enabled ? '#8e8e93' : '#d1d1d6',

        '&:hover': {
            backgroundColor: enabled ? 'rgba(142, 142, 147, 0.1)' : 'transparent',
        }
    }),

    '& .MuiSvgIcon-root': {
        fontSize: '20px'
    },

    '@media (max-width: 360px)': {
        width: '32px',
        height: '32px',

        '& .MuiSvgIcon-root': {
            fontSize: '18px'
        }
    },

    '@media (max-width: 320px)': {
        width: '28px',
        height: '28px',

        '& .MuiSvgIcon-root': {
            fontSize: '16px'
        }
    }
}));

const PremiumNextButton = styled(Button)({
    alignSelf: 'flex-start',
    marginLeft: '0px',
    backgroundColor: '#A1B7A5',
    color: '#ffffff',
    borderRadius: '18px',
    padding: '10px 24px',
    fontSize: '15px',
    fontWeight: '600',
    minWidth: '120px',
    textTransform: 'none',
    boxShadow: '0 2px 8px rgba(161, 183, 165, 0.25)',

    '&:hover': {
        backgroundColor: '#8FA599',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(161, 183, 165, 0.35)',
    },

    '&:active': {
        transform: 'translateY(0)',
    },

    '&:disabled': {
        backgroundColor: '#d1d1d6',
        color: '#8e8e93',
        transform: 'none',
        boxShadow: 'none',
    },

    '@media (max-width: 360px)': {
        fontSize: '14px',
        padding: '8px 20px',
        borderRadius: '16px',
        marginLeft: '0px',
        minWidth: '100px',
    },

    '@media (max-width: 320px)': {
        fontSize: '13px',
        padding: '6px 16px',
        borderRadius: '14px',
        marginLeft: '0px',
        minWidth: '80px',
    }
});
interface MobileChatProps {
    messagesData: Array<FeedbackInterface | ParagraphInterface>;
    onUserInput: (msg: string) => void;
    isLoading: boolean;
    handleNext?: (outputs: string) => void;
    onSubmit: () => void;
    handleSaveProgress: (data: { selectedOptions?: string[]; points: number; placedElements?: Record<string, string[]> }) => void;
    clickedIndexes: Set<number>;
    setClickedIndexes: React.Dispatch<React.SetStateAction<Set<number>>>;
    handleNextChapter?: (sectionId: number) => void;
    handleFeedback?: (sectionId: number, rating: number) => void;
    npsSubmitted: boolean;
    setNpsSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
    typingAllowed: boolean;
    chapter?: {
        id: string;
        title: string;
        num: number;
        isBookmarked: boolean;
        isLastSection: boolean;
        thumbnail?: string;
    };
}

const MobileChatComponent: React.FC<MobileChatProps> = ({
    messagesData,
    onUserInput,
    isLoading,
    handleNext,
    onSubmit,
    handleSaveProgress,
    clickedIndexes,
    setClickedIndexes,
    handleNextChapter,
    handleFeedback,
    npsSubmitted,
    setNpsSubmitted,
    typingAllowed,
    chapter,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [messageText, setMessageText] = useState('');
    const [focused, setFocused] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [selectedNps, setSelectedNps] = useState<number | null>(null);

    useEffect(() => {
        localStorage.setItem('clickedIndexes', JSON.stringify(Array.from(clickedIndexes)));
    }, [clickedIndexes]);

    const getNpsColor = (num: number) => {
        if (num >= 0 && num <= 6) return "#FF3B30";
        if (num >= 7 && num <= 8) return "#FF9500";
        if (num >= 9 && num <= 10) return "#A1B7A5";
        return "#d1d1d6";
    };

    const handleNextButtonClick = (id: number, outputs: string) => {
        setClickedIndexes(prev => {
            const updated = new Set(prev);
            updated.add(id);
            setTimeout(() => {
                handleNext?.(outputs);
            }, 0);
            return updated;
        });
    };

    const filteredMessages = messagesData.filter((msg, index, self) => {
        if (!('type' in msg) || msg.type !== 'next') return true;
        const lastNextIndex = [...self].reverse().findIndex(m => 'type' in m && m.type === 'next');
        const actualLastIndex = self.length - 1 - lastNextIndex;
        return index === actualLastIndex;
    });

    useLayoutEffect(() => {
        if (containerRef.current) {
            const scrollContainer = containerRef.current;
            setTimeout(() => {
                scrollContainer.scrollTo({
                    top: scrollContainer.scrollHeight,
                    behavior: 'smooth'
                });
            }, 50);
        }
    }, [messagesData, isLoading]);

    function isFeedbackInterface(obj: any): obj is FeedbackInterface {
        return 'feedback' in obj;
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (messageText.trim() && typingAllowed) {
            onUserInput(messageText.trim());
            setMessageText('');
            setFocused(false);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (messageText.trim() && typingAllowed) {
                onUserInput(messageText.trim());
                setMessageText('');
                setFocused(false);
            }
        }
    };

    function isParagraphInterface(message: FeedbackInterface | ParagraphInterface): message is ParagraphInterface {
        return 'type' in message && 'content' in message && typeof message.content === 'object';
    }

    function hasContent(message: FeedbackInterface | ParagraphInterface): message is ParagraphInterface {
        return (message as ParagraphInterface).content !== undefined;
    }

    const showSendButton = messageText.trim() && typingAllowed;
    return (
        <UltraModernWrapper>
            <ModernContentBox>
                <PremiumChatBox ref={containerRef}>
                    {chapter && (
                        <Box sx={{
                            padding: '16px 0',
                            marginBottom: '20px',
                            position: 'relative',
                            width: '100%',

                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: '20%',
                                right: '20%',
                                height: '2px',
                                background: 'linear-gradient(90deg, transparent 0%, #A1B7A5 50%, transparent 100%)',
                                borderRadius: '1px',
                            },

                            '@media (max-width: 360px)': {
                                padding: '14px 0',
                                marginBottom: '16px',

                                '&::after': {
                                    left: '15%',
                                    right: '15%',
                                }
                            },

                            '@media (max-width: 320px)': {
                                padding: '12px 0',
                                marginBottom: '14px',

                                '&::after': {
                                    left: '10%',
                                    right: '10%',
                                }
                            }
                        }}>
                            <Typography sx={{
                                fontSize: '19px',
                                fontWeight: '700',
                                color: '#1a202c',
                                marginBottom: '8px',
                                textAlign: 'center',
                                letterSpacing: '0.3px',

                                '@media (max-width: 360px)': {
                                    fontSize: '17px',
                                    marginBottom: '6px',
                                },

                                '@media (max-width: 320px)': {
                                    fontSize: '16px',
                                    letterSpacing: '0.2px',
                                }
                            }}>
                                {t(chapter.title)}
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                            }}>
                                <Box sx={{
                                    width: '24px',
                                    height: '1px',
                                    backgroundColor: '#cbd5e0',

                                    '@media (max-width: 320px)': {
                                        width: '20px',
                                    }
                                }} />

                                <Typography sx={{
                                    fontSize: '12px',
                                    color: '#A1B7A5',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1.2px',
                                    padding: '4px 12px',
                                    backgroundColor: '#f7fafc',
                                    borderRadius: '12px',
                                    border: '1px solid #e2e8f0',

                                    '@media (max-width: 320px)': {
                                        fontSize: '11px',
                                        letterSpacing: '1px',
                                        padding: '3px 10px',
                                    }
                                }}>
                                    Kapitel {chapter.num}
                                </Typography>

                                <Box sx={{
                                    width: '24px',
                                    height: '1px',
                                    backgroundColor: '#cbd5e0',

                                    '@media (max-width: 320px)': {
                                        width: '20px',
                                    }
                                }} />
                            </Box>
                        </Box>
                    )}
                    {filteredMessages.map((message, index) => {
                        const key = message.id;

                        if (isParagraphInterface(message) && message.type === 'mc') {
                            const content = message.content as unknown as MultipleChoiceData;
                            return (
                                <Box key={key} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    <ChatBubble
                                        date={message.time}
                                        message={<MultipleChoice
                                            question={content.question}
                                            options={content.options}
                                            maxPoints={content.maxPoints}
                                            negativePoints={content.negativePoints}
                                            feedback={content.feedback}
                                            onChange={(selectedOptions) => console.log('Selected options:', selectedOptions)}
                                            isSingleChoice={content.isSingleChoice}
                                            onSubmit={onSubmit}
                                            onSaveProgress={handleSaveProgress}
                                            data={{
                                                selectedOptions: message.progressState?.selectedOptions ?? [],
                                                points: message.progressState?.points ?? 0
                                            }}
                                        />}
                                        isLeft={true}
                                        isMobile={true}
                                    />
                                </Box>
                            );
                        }

                        else if (isParagraphInterface(message) && message.type === 'io' &&
                            [message.title === 'Multiple Choice' || message.title === 'Multiple-Choice']) {
                            const content = message.content as unknown as MultipleChoiceData;
                            return (
                                <Box key={key} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    <ChatBubble
                                        date={message.time}
                                        message={
                                            <MultipleChoice
                                                question={content.question}
                                                options={content.options}
                                                maxPoints={content.maxPoints}
                                                negativePoints={content.negativePoints}
                                                feedback={content.feedback}
                                                onChange={(selectedOptions) => console.log('Selected options:', selectedOptions)}
                                                isSingleChoice={content.isSingleChoice}
                                                onSubmit={onSubmit}
                                                onSaveProgress={handleSaveProgress}
                                                data={{
                                                    selectedOptions: message.progressState?.selectedOptions ?? [],
                                                    points: message.progressState?.points ?? 0
                                                }}
                                            />
                                        }
                                        isLeft={true}
                                        isMobile={true}
                                    />
                                </Box>
                            );
                        }

                        else if (isParagraphInterface(message) && message.type === 'io' && message.title === 'GPT') {
                            const jsonObject = parseHTMLToJSON(message.content);
                            return (
                                <Box key={key} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    <ChatBubble
                                        date={message.time}
                                        message={jsonObject}
                                        isLeft={true}
                                        isMobile={true}
                                    />
                                </Box>
                            );
                        }

                        else if (hasContent(message) && message.type === 'io' &&
                            (message.title === "Drag-and-Drop" || message.title === "Drag-and-Drop-Multiple")) {
                            return (
                                <Box key={key} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    <ChatBubble
                                        date={message.time}
                                        disabled={index < filteredMessages.length - 2 || isLoading}
                                        message={<DragAndDrop
                                            htmlContent={message.content}
                                            onSubmit={onSubmit}
                                            onSaveProgress={(data) => handleSaveProgress({
                                                selectedOptions: data.selectedOptions ?? [],
                                                points: data.points,
                                                placedElements: data.placedElements
                                            })}
                                            data={message.progressState || { placedElements: {}, points: 0 }}
                                        />}
                                        isLeft={true}
                                        isMobile={true}
                                        handleNext={handleNext}
                                    />
                                </Box>
                            );
                        }

                        else if (hasContent(message) && message.type === 'io' &&
                            (message.title === "Sort-the-Paragraph" || message.title === "Sort")) {
                            return (
                                <Box key={key} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    <ChatBubble
                                        date={message.time}
                                        disabled={index < filteredMessages.length - 2 || isLoading}
                                        message={
                                            <SortTheParagraph
                                                htmlContent={message.content}
                                                onSubmit={onSubmit}
                                                onSaveProgress={(data) => handleSaveProgress({
                                                    selectedOptions: data.selectedOptions ?? [],
                                                    points: data.points,
                                                    placedElements: data.placedElements
                                                })}
                                                data={{
                                                    placedElements: message.progressState?.placedElements ?? {},
                                                    points: message.progressState?.points ?? 0
                                                }}
                                            />
                                        }
                                        isLeft={true}
                                        isMobile={true}
                                        handleNext={handleNext}
                                    />
                                </Box>
                            );
                        }

                        else if (isFeedbackInterface(message) || message.type === 'io') {
                            return (
                                <Box key={key} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    <ChatBubble
                                        date={message.time}
                                        disabled={index < filteredMessages.length - 2 || isLoading}
                                        message={isFeedbackInterface(message) ? message.feedback : message.content}
                                        isLeft={isFeedbackInterface(message) ? message.direction === '0' : true}
                                        outputs={!isFeedbackInterface(message) && message.outputs}
                                        handleNext={handleNext}
                                        isMobile={true}
                                    />
                                </Box>
                            );
                        }
                        if (message.type === 'next') {
                            return (
                                !clickedIndexes.has(message.id) && (
                                    <Box key={key} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                        <PremiumNextButton
                                            disabled={index < filteredMessages.length - 1 || isLoading}
                                            onClick={() => handleNextButtonClick(message.id, message.outputs)}
                                        >
                                            {t("Weiter")}
                                        </PremiumNextButton>
                                    </Box>
                                )
                            );
                        }

                        else if (message.type === 'chat') {
                            return (
                                <Box key={key} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    <ChatBubble
                                        date={message.time}
                                        disabled={index < filteredMessages.length - 2 || isLoading}
                                        message={isFeedbackInterface(message) ? message.feedback : message.content}
                                        isLeft={isFeedbackInterface(message) ? message.direction === '0' : true}
                                        outputs={!isFeedbackInterface(message) && message.outputs}
                                        handleNext={handleNext}
                                        isMobile={true}
                                    />
                                </Box>
                            );
                        }

                        else if (message.type === 'userInput') {
                            const userMessage = typeof message.content === 'string' && message.content.trim() !== ""
                                ? message.content
                                : '[Leere Eingabe]';
                            return (
                                <PremiumUserBox key={key}>
                                    <PremiumUserContent>
                                        {userMessage}
                                    </PremiumUserContent>
                                </PremiumUserBox>
                            );
                        }

                        else if (message.type === 'response') {
                            return (
                                <Box key={key} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    <ChatBubble
                                        date={message.time}
                                        disabled={index < filteredMessages.length - 2 || isLoading}
                                        message={message.content || "[Keine Antwort erhalten]"}
                                        isLeft={true}
                                        handleNext={handleNext}
                                        isMobile={true}
                                    />
                                </Box>
                            );
                        }
                        else if (message.type === "exit") {
                            return chapter?.isLastSection ? (
                                <Box key={key} sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    padding: { xs: '20px 16px', sm: '24px 20px' },
                                    gap: { xs: '16px', sm: '20px' },
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    borderRadius: '16px',
                                    margin: { xs: '8px 12px', sm: '8px 16px' },
                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                                    backdropFilter: 'blur(10px)',
                                    width: '100%',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box',

                                    '@media (max-width: 320px)': {
                                        padding: '16px 12px',
                                        gap: '12px',
                                        margin: '6px 8px',
                                        borderRadius: '14px',
                                    }
                                }}>
                                    {!npsSubmitted ? (
                                        <>
                                            <Typography sx={{
                                                fontSize: { xs: '16px', sm: '17px' },
                                                fontWeight: '600',
                                                color: '#1d1d1f',
                                                textAlign: 'center',
                                                lineHeight: '1.4',

                                                '@media (max-width: 320px)': {
                                                    fontSize: '15px',
                                                    lineHeight: '1.3',
                                                }
                                            }}>
                                                {t("How likely are you to recommend this course?")}
                                            </Typography>

                                            <Box sx={{
                                                display: 'grid',
                                                gridTemplateColumns: {
                                                    xs: 'repeat(6, 1fr)',
                                                    sm: 'repeat(11, 1fr)'
                                                },
                                                gap: { xs: '6px', sm: '8px' },
                                                width: '100%',
                                                maxWidth: { xs: '280px', sm: '340px' },

                                                '@media (max-width: 320px)': {
                                                    gridTemplateColumns: 'repeat(6, 1fr)',
                                                    gap: '4px',
                                                    maxWidth: '260px',
                                                }
                                            }}>
                                                {Array.from({ length: 11 }, (_, num) => (
                                                    <Button
                                                        key={num}
                                                        variant={selectedNps === num ? "contained" : "outlined"}
                                                        onClick={() => setSelectedNps(num)}
                                                        sx={{
                                                            backgroundColor: selectedNps === num ? getNpsColor(num) : 'transparent',
                                                            color: selectedNps === num ? '#ffffff' : '#1d1d1f',
                                                            border: `2px solid ${selectedNps === num ? getNpsColor(num) : '#d1d1d6'}`,
                                                            minWidth: {
                                                                xs: "36px",
                                                                sm: "44px"
                                                            },
                                                            minHeight: {
                                                                xs: "36px",
                                                                sm: "44px"
                                                            },
                                                            fontSize: {
                                                                xs: "14px",
                                                                sm: "16px"
                                                            },
                                                            borderRadius: { xs: "10px", sm: "12px" },
                                                            fontWeight: '600',
                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

                                                            '&:hover': {
                                                                backgroundColor: selectedNps === num ? getNpsColor(num) : `${getNpsColor(num)}15`,
                                                                borderColor: getNpsColor(num),
                                                                transform: 'scale(1.05)',
                                                            },

                                                            '@media (max-width: 320px)': {
                                                                minWidth: '32px',
                                                                minHeight: '32px',
                                                                fontSize: '13px',
                                                                borderRadius: '8px',
                                                                borderWidth: '1.5px',
                                                            }
                                                        }}
                                                    >
                                                        {num}
                                                    </Button>
                                                ))}
                                            </Box>

                                            <Button
                                                disabled={selectedNps === null || isLoading}
                                                onClick={() => {
                                                    if (selectedNps !== null && handleFeedback) {
                                                        handleFeedback((message as any).section_id, selectedNps);
                                                        setNpsSubmitted(true);

                                                        window.dispatchEvent(new CustomEvent('courseCompleted', {
                                                            detail: { skillId: chapter?.id }
                                                        }));
                                                        // Aktualisiere Chat Message direkt
                                                        const completedPreview = {
                                                            content: 'Kurs abgeschlossen',
                                                            timestamp: new Date().toISOString(),
                                                            type: 'exit',
                                                            id: (message as any).id
                                                        };
                                                        localStorage.setItem(`lastChatMessage_${chapter?.id}`, JSON.stringify(completedPreview));
                                                    }
                                                }}
                                                sx={{
                                                    backgroundColor: '#A1B7A5',
                                                    color: '#ffffff',
                                                    borderRadius: { xs: '10px', sm: '12px' },
                                                    padding: {
                                                        xs: '10px 24px',
                                                        sm: '12px 28px'
                                                    },
                                                    fontSize: { xs: '15px', sm: '16px' },
                                                    fontWeight: '600',
                                                    textTransform: 'none',
                                                    minWidth: { xs: '120px', sm: '140px' },
                                                    boxShadow: '0 2px 8px rgba(161, 183, 165, 0.25)',

                                                    '&:hover': {
                                                        backgroundColor: '#8FA599',
                                                        boxShadow: '0 4px 12px rgba(161, 183, 165, 0.35)',
                                                    },

                                                    '&:disabled': {
                                                        backgroundColor: '#d1d1d6',
                                                        color: '#8e8e93',
                                                        boxShadow: 'none',
                                                    },

                                                    '@media (max-width: 320px)': {
                                                        padding: '8px 20px',
                                                        fontSize: '14px',
                                                        minWidth: '100px',
                                                        borderRadius: '8px',
                                                    }
                                                }}
                                            >
                                                {t("Submit")}
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <ChatBubble
                                                message={t("Thank you for your feedback!")}
                                                isLeft={true}
                                                isMobile={true}
                                            />
                                            <Button
                                                onClick={() => window.location.href = "/skillary"}
                                                sx={{
                                                    backgroundColor: '#A1B7A5',
                                                    color: '#ffffff',
                                                    borderRadius: { xs: '10px', sm: '12px' },
                                                    padding: {
                                                        xs: '10px 24px',
                                                        sm: '12px 28px'
                                                    },
                                                    fontSize: { xs: '15px', sm: '16px' },
                                                    fontWeight: '600',
                                                    textTransform: 'none',
                                                    minWidth: { xs: '140px', sm: '160px' },
                                                    boxShadow: '0 2px 8px rgba(161, 183, 165, 0.25)',

                                                    '&:hover': {
                                                        backgroundColor: '#8FA599',
                                                        boxShadow: '0 4px 12px rgba(161, 183, 165, 0.35)',
                                                    },

                                                    '@media (max-width: 320px)': {
                                                        padding: '8px 20px',
                                                        fontSize: '14px',
                                                        minWidth: '120px',
                                                        borderRadius: '8px',
                                                    }
                                                }}
                                            >
                                                {t("goToSkillary")}
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            ) : (
                                <Box key={key} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    <PremiumNextButton
                                        disabled={index < filteredMessages.length - 1 || isLoading}
                                        onClick={() => handleNextChapter && handleNextChapter((message as any).section_id)}
                                    >
                                        {t("Next Chapter")}
                                    </PremiumNextButton>
                                </Box>
                            );
                        }

                        return null;
                    })}
                    {isLoading && <MobileChatLoader />}
                </PremiumChatBox>
            </ModernContentBox>

            <UltraInputContainer>
                <MinimalDisclaimer>
                    {t('KI Disclamer')}
                </MinimalDisclaimer>

                <form onSubmit={handleSubmit}>
                    <PremiumInputWrapper
                        focused={focused}
                        enabled={typingAllowed}
                    >
                        <PremiumTextField
                            multiline
                            maxRows={4}
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            onFocus={() => setFocused(true)}
                            onBlur={() => setFocused(false)}
                            placeholder={typingAllowed ? t('Enter message') : t('Waiting for prompt')}
                            variant="standard"
                            fullWidth
                            enabled={typingAllowed}
                            disabled={!typingAllowed}
                        />

                        {showSendButton ? (
                            <PremiumActionButton
                                type="submit"
                                variant="send"
                                disabled={!messageText.trim() || !typingAllowed}
                            >
                                <Send />
                            </PremiumActionButton>
                        ) : (
                            <PremiumActionButton
                                variant="mic"
                                enabled={typingAllowed}
                                disabled
                            >
                                <Mic />
                            </PremiumActionButton>
                        )}
                    </PremiumInputWrapper>
                </form>
            </UltraInputContainer>
        </UltraModernWrapper>
    );
};

export default MobileChatComponent;